import { CLIENT_HEADER, PORTAL_TOKEN_HEADER, buildPath, routes, type RouteKey } from '@garage/shared';
import { ApiError } from '../../shared/api.js';

/**
 * Vague 6 : téléchargement d'un fichier servi par l'API (PDF de la fiche de réception, archive d'export).
 * Le fichier est lu par `fetch` (en-tête client, jeton du portail en en-tête : jamais dans l'adresse), puis
 * remis au navigateur sous le nom proposé par le serveur (`content-disposition`), neutre (aucun nom ni plaque).
 */
export async function downloadRoute(
  key: RouteKey,
  params: Record<string, string> | undefined,
  opts: { fallbackName: string; portalToken?: string | null; /** 03/10/2026 : paramètres de requête (export de Gestion). */ query?: Record<string, string | undefined> },
): Promise<string> {
  const def = routes[key];
  const headers: Record<string, string> = { [CLIENT_HEADER]: 'web' };
  if (opts.portalToken) headers[PORTAL_TOKEN_HEADER] = opts.portalToken;
  const search = new URLSearchParams(Object.entries(opts.query ?? {}).filter((e): e is [string, string] => e[1] !== undefined && e[1] !== '')).toString();
  const res = await fetch(`${buildPath(def.path, params)}${search ? `?${search}` : ''}`, { method: def.method, headers, credentials: 'same-origin' });
  if (!res.ok) {
    const err = (await res.json().catch(() => null)) as { error?: { code?: string; message?: string } } | null;
    throw new ApiError(res.status, err?.error?.code ?? 'http_error', err?.error?.message ?? res.statusText);
  }
  const name = fileNameOf(res.headers.get('content-disposition')) ?? opts.fallbackName;
  const blob = await res.blob();
  saveBlob(blob, name);
  return name;
}

/** Nom de fichier d'un en-tête `content-disposition` (`attachment; filename="…"`). */
export function fileNameOf(header: string | null): string | null {
  const m = header ? /filename="?([^";]+)"?/i.exec(header) : null;
  const name = m?.[1]?.trim();
  // Nom simple seulement (aucun chemin).
  return name && /^[\w.-]+$/.test(name) ? name : null;
}

function saveBlob(blob: Blob, name: string): void {
  if (typeof URL.createObjectURL !== 'function') return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
